import React, { FC, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { CommonInput, FlexBox, INPUT_TYPES, useToasts } from '@brg/ui';
import styled from 'styled-components';
import { LayoutConst, useModal } from 'core';
import { isValidUrl } from 'shared/pages/projectEdit/components/LMS/LessonsEditor/helpers';
import { normalizeUrl } from 'shared/helpers/normalizeUrl';
import {
  CalendarLabel,
  ClockIcon,
  DayButton,
  DaysContainer,
  GroupButton,
  GroupsContainer,
  GroupsRow,
  LessonTypeWrapper,
  ModalTimeAndTypeRow,
  StyledRectangularButtonFilled,
  ZoomLink,
} from 'shared/pages/projectEdit/components/LMS/LessonsEditor/styles';
import { ILessonData, ILessonModalData, LMSLessonClassTypeModel } from 'shared/pages/projectEdit/components/LMS/LessonsEditor/LessonsEditorTypes';

const ModalWrapper = styled.div<{ $inline?: boolean }>`
  border-radius: 8px;
  width: 720px;
  padding: 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${({ $inline }) => $inline && `
    width: 100%;
    padding: 0;
    border-radius: 0;
    gap: 16px;
  `}

`;

const ModalHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;

const ModalCloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.5;
  }
`;
const ModalTitleText = styled.div`
  font-family: Inter;
  font-size: 28px;
  font-weight: 600;
  line-height: 34px;
  letter-spacing: -0.56px;
  color: #151514;

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 18px;
    line-height: 24px;
  }
`;

const ModalSubtitleText = styled.div`
  font-family: Inter;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  color: #909090;
  letter-spacing: -0.3px;
  margin-top: 4px;
`;
const InlineLessonFieldsRow = styled.div`
  display: grid;
  grid-template-columns: 336px 140px 140px minmax(180px, 1fr);
  gap: 24px;
  align-items: end;
  width: 100%;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    grid-template-columns: 280px 100px 100px minmax(160px, 1fr);
    gap: 16px;
  }

  @media (max-width: ${LayoutConst.screen.md}px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    > :first-child,
    > :last-child {
      grid-column: span 2;
    }
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    grid-template-columns: 1fr;

    > :first-child,
    > :last-child {
      grid-column: auto;
    }
  }
`;

const TimeInputWrapper = styled(FlexBox)<{ $inline?: boolean }>`
  position: relative;
  width: 120px;
  height: 48px;
  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    width: 100px;
    height: 40px;
    }
  @media (max-width: ${LayoutConst.screen.m}px) {
      width: 195px;
    }
  @media (max-width: ${LayoutConst.screen.s}px) {
      width: 100%;
    }

  input[type="time"] {
    width: 100%;
    padding: 14px 16px;
    font-family: Inter;
    font-size: 15px;
    font-weight: 400;
    line-height: 20px;
    color: #151514;
    background-color: ${({ $inline }) => ($inline ? '#FFFFFF' : '#F2F2F2')};
    border: none;
    border-radius: 3px;
    outline: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;

    &::-webkit-calendar-picker-indicator {
      display: none;
    }

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
      font-size: 13px;
      line-height: 16px;
      padding: 10px 36px 10px 14px;
    }

  }
`;
const ZoomInputWrapper = styled(FlexBox)<{ $inline?: boolean }>`
  width: 185px;
  height: 48px;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    width: 150px;
    height: 40px;
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    width: 100%;
  }

  input {
    width: 100%;
    padding: 12px 16px;
    font-family: Inter;
    font-size: 15px;
    font-weight: 400;
    line-height: 20px;
    color: #151514;
    background-color: ${({ $inline }) => ($inline ? '#FFFFFF' : '#F2F2F2')};
    border: none;
    border-radius: 3px;
    outline: none;

    &::placeholder {
      color: #909090;
    }

    @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
      font-size: 13px;
      line-height: 16px;
      padding: 10px 14px;
    }
  }
`;

const ClockIconComponent: FC<{ onClick?: () => void }> = ({ onClick }) => (
  <ClockIcon onClick={onClick}>
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0ZM8 14.5C4.41015 14.5 1.5 11.5899 1.5 8C1.5 4.41015 4.41015 1.5 8 1.5C11.5899 1.5 14.5 4.41015 14.5 8C14.5 11.5899 11.5899 14.5 8 14.5Z" fill="#909090"/>
      <path d="M8.5 4H7.5V8.5H11.5V7.5H8.5V4Z" fill="#909090"/>
    </svg>
  </ClockIcon>
);


export interface LessonAddModalProps {
  courseStartDate: Date | null;
  courseEndDate: Date | null;
  courseGroups: Array<{ groupId: string; name: Array<{ lang: string; name: string }> }>;
  lessonTypeOptions: Array<{ value: string; label: string }>;
  getGroupName: (group: { groupId: string; name: Array<{ lang: string; name: string }> }) => string;
  onSubmit: (data: ILessonData, onSuccess: () => void) => void;
  inline?: boolean;
}

export const LessonAddModal: FC<LessonAddModalProps> = ({
  courseStartDate,
  courseEndDate,
  courseGroups,
  lessonTypeOptions,
  getGroupName,
  onSubmit,
  inline = false,
}) => {
  const { t } = useTranslation();
  const modal = useModal();
  const toasts = useToasts();

  const startTimeRef = useRef<HTMLInputElement>(null);
  const endTimeRef = useRef<HTMLInputElement>(null);

  const { control, watch, setValue, handleSubmit } = useForm<ILessonModalData>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    shouldUnregister: false,
    defaultValues: {
      selectedDays: [],
      start: '',
      end: '',
      zoomLink: '',
      selectedGroups: [],
      selectedLessonType: LMSLessonClassTypeModel.Ordinal,
    },
  });

  const formData = watch();

  const handleDaySelect = useCallback((dayValue: number) => {
    const currentDays = watch('selectedDays') || [];
    const isSelected = currentDays.includes(dayValue);
    setValue(
      'selectedDays',
      isSelected ? currentDays.filter((d) => d !== dayValue) : [...currentDays, dayValue],
      { shouldValidate: false, shouldDirty: false, shouldTouch: false },
    );
  }, [watch, setValue]);

  const handleGroupToggle = useCallback((groupId: string) => {
    const currentGroups = watch('selectedGroups') || [];
    const isSelected = currentGroups.includes(groupId);
    setValue(
      'selectedGroups',
      isSelected ? currentGroups.filter((id) => id !== groupId) : [...currentGroups, groupId],
      { shouldValidate: false, shouldDirty: false, shouldTouch: false },
    );
  }, [watch, setValue]);

  const handleStartTimeClick = useCallback(() => {
    startTimeRef.current?.showPicker?.();
    startTimeRef.current?.focus();
  }, []);

  const handleEndTimeClick = useCallback(() => {
    endTimeRef.current?.showPicker?.();
    endTimeRef.current?.focus();
  }, []);

  const handleLocalSubmit = useCallback((data: ILessonModalData) => {
    onSubmit(
      {
        ...data,
        courseStartDate,
        courseEndDate,
      },
      () => {
        if (!inline) {
          modal.hide();
        }
      },
    );
  }, [onSubmit, courseStartDate, courseEndDate, inline, modal]);

  const daysField = (
    <FlexBox direction="column" gy={8}>
      <CalendarLabel>{t('lms.lessonsEditor.selectDayLabel')}</CalendarLabel>
      <DaysContainer style={{ height: 'auto', flexWrap: 'wrap' }}>
        {[
          { label: t('lms.lessonsEditor.weekDays.mon'), value: 1 },
          { label: t('lms.lessonsEditor.weekDays.tue'), value: 2 },
          { label: t('lms.lessonsEditor.weekDays.wed'), value: 3 },
          { label: t('lms.lessonsEditor.weekDays.thu'), value: 4 },
          { label: t('lms.lessonsEditor.weekDays.fri'), value: 5 },
          { label: t('lms.lessonsEditor.weekDays.sat'), value: 6 },
          { label: t('lms.lessonsEditor.weekDays.sun'), value: 0 },
        ].map((day) => (
          <DayButton
            key={day.value}
            isSelected={formData.selectedDays.includes(day.value)}
            onClick={() => handleDaySelect(day.value)}
          >
            {day.label}
          </DayButton>
        ))}
      </DaysContainer>
    </FlexBox>
  );

  const startTimeField = (
    <FlexBox direction="column" gy={8}>
      <CalendarLabel>{t('lms.lessonsEditor.startTimeLabel')}</CalendarLabel>
      <TimeInputWrapper $inline={inline} style={{ width: '100%' }}>
        <Controller
          name="start"
          control={control}
          render={({ field }) => (
            <input
              type="time"
              value={field.value}
              ref={(element) => {
                field.ref(element);
                (startTimeRef as React.MutableRefObject<HTMLInputElement | null>).current = element;
              }}
              onChange={(event) => {
                const newStartTime = event.target.value;
                const endTime = watch('end');
                if (endTime && newStartTime && endTime < newStartTime) {
                  setValue('end', '', { shouldValidate: false, shouldDirty: false });
                  toasts.info(t('lms.lessonsEditor.endTimeResetInfo'));
                }
                field.onChange(newStartTime);
              }}
            />
          )}
        />
        <ClockIconComponent onClick={handleStartTimeClick} />
      </TimeInputWrapper>
    </FlexBox>
  );

  const endTimeField = (
    <FlexBox direction="column" gy={8}>
      <CalendarLabel>{t('lms.lessonsEditor.endTimeLabel')}</CalendarLabel>
      <TimeInputWrapper $inline={inline} style={{ width: '100%' }}>
        <Controller
          name="end"
          control={control}
          render={({ field }) => (
            <input
              type="time"
              value={field.value}
              min={formData.start || undefined}
              ref={(element) => {
                field.ref(element);
                (endTimeRef as React.MutableRefObject<HTMLInputElement | null>).current = element;
              }}
              onChange={(event) => {
                const newEndTime = event.target.value;
                const startTime = watch('start');
                if (startTime && newEndTime && newEndTime < startTime) {
                  toasts.error(t('lms.lessonsEditor.endTimeBeforeStartError'));
                  field.onChange('');
                  return;
                }
                field.onChange(newEndTime);
              }}
            />
          )}
        />
        <ClockIconComponent onClick={handleEndTimeClick} />
      </TimeInputWrapper>
    </FlexBox>
  );

  const zoomLinkField = (
    <FlexBox direction="column" gy={8}>
      <CalendarLabel>{t('lms.lessonsEditor.zoomLinkLabel')}</CalendarLabel>
      <ZoomInputWrapper $inline={inline} style={{ width: '100%' }}>
        <Controller
          name="zoomLink"
          control={control}
          render={({ field }) => (
            <input
              type="text"
              placeholder={t('lms.lessonsEditor.enterData')}
              {...field}
              onChange={(event) => {
                const filteredValue = event.target.value.replace(/[А-Яа-яЁё]/g, '');
                field.onChange(filteredValue);
              }}
            />
          )}
        />
      </ZoomInputWrapper>
      {formData.zoomLink && isValidUrl(formData.zoomLink) && (
        <ZoomLink
          href={normalizeUrl(formData.zoomLink)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => event.stopPropagation()}
          style={{ maxWidth: '100%' }}
        >
          {normalizeUrl(formData.zoomLink)}
        </ZoomLink>
      )}
    </FlexBox>
  );

  return (
    <ModalWrapper $inline={inline}>
      {!inline && (
        <ModalHeaderRow>
          <div>
            <ModalTitleText>{t('lms.lessonsEditor.modalTitle')}</ModalTitleText>
            <ModalSubtitleText>{t('lms.lessonsEditor.subtitle')}</ModalSubtitleText>
          </div>
          <ModalCloseBtn onClick={() => modal.hide()} aria-label="close">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M0.298618 0.154297C0.504347 -0.0514325 0.837901 -0.0514325 1.04363 0.154297L8.57311 7.68378L16.4561 0.154298C16.6619 -0.0514323 16.9954 -0.0514321 17.2012 0.154298L17.3455 0.298619C17.5512 0.504348 17.5512 0.8379 17.3455 1.04363L9.816 8.57311C9.76911 8.61999 9.74277 8.68358 9.74277 8.74989C9.74277 8.81619 9.76911 8.87978 9.816 8.92666L17.3455 16.4561C17.5524 16.6631 17.5507 16.9967 17.3451 17.1956C17.2768 17.2616 17.2393 17.2967 17.2177 17.316C16.9766 17.5327 16.643 17.5324 16.4419 17.3312L8.92666 9.81599C8.82903 9.71836 8.67074 9.71836 8.57311 9.81599L1.04364 17.3455C0.836674 17.5524 0.503017 17.5507 0.30421 17.3451C0.238131 17.2768 0.203119 17.2393 0.183765 17.2177C-0.0329741 16.9766 -0.0326004 16.643 0.16857 16.4419L7.68377 8.92666C7.73066 8.87978 7.757 8.81619 7.757 8.74989C7.757 8.68358 7.73066 8.61999 7.68378 8.57311L0.154297 1.04363C-0.0514323 0.837901 -0.0514324 0.504348 0.154297 0.298618L0.298618 0.154297Z" fill="#151514"/>
            </svg>
          </ModalCloseBtn>
        </ModalHeaderRow>
      )}

      {inline ? (
        <InlineLessonFieldsRow>
          {daysField}
          {startTimeField}
          {endTimeField}
          {zoomLinkField}
        </InlineLessonFieldsRow>
      ) : (
        <>
          {daysField}
          <ModalTimeAndTypeRow>
            {startTimeField}
            {endTimeField}
            <FlexBox direction="column" gy={8}>
              <CalendarLabel>{t('lms.lessonsEditor.lessonTypeLabel')}</CalendarLabel>
              <LessonTypeWrapper style={{ width: '100%' }}>
                <Controller
                  name="selectedLessonType"
                  control={control}
                  render={({ field }) => (
                    <CommonInput
                      type={INPUT_TYPES.dropdown}
                      orientation="column"
                      options={lessonTypeOptions}
                      value={lessonTypeOptions.find((option) => option.value === String(field.value)) || undefined}
                      onChange={(option: any) => {
                        field.onChange(Number(option?.value ?? LMSLessonClassTypeModel.Ordinal));
                      }}
                      placeholder={t('lms.lessonsEditor.selectLessonType')}
                    />
                  )}
                />
              </LessonTypeWrapper>
            </FlexBox>
            {zoomLinkField}
          </ModalTimeAndTypeRow>
        </>
      )}

      {/* Группы */}
      <FlexBox direction="column" gy={8}>
        <CalendarLabel>{t('lms.lessonsEditor.groupsLabel')}</CalendarLabel>
        <GroupsContainer style={{ maxWidth: '100%' }}>
          <GroupsRow>
            {courseGroups.map((group) => (
              <GroupButton
                key={group.groupId}
                isSelected={formData.selectedGroups.includes(group.groupId)}
                $inline={inline}
                onClick={() => handleGroupToggle(group.groupId)}
              >
                {(() => {
                  const name = getGroupName(group);
                  return name.length > 15 ? `${name.substring(0, 15)}...` : name;
                })()}
              </GroupButton>
            ))}
          </GroupsRow>
        </GroupsContainer>
      </FlexBox>

      <StyledRectangularButtonFilled
        style={{
          width: inline ? '390px' : '100%',
          alignSelf: inline ? 'flex-end' : undefined,
        }}
        onClick={handleSubmit(handleLocalSubmit)}
      >
        {t('lms.lessonsEditor.addLessonButton')}
      </StyledRectangularButtonFilled>

    </ModalWrapper>
  );
};
import React, { FC, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { CommonInput, FlexBox, INPUT_TYPES, useToasts } from '@brg/ui';
import styled from 'styled-components';
import { LayoutConst, useModal } from 'core';
import { isValidUrl } from 'shared/pages/projectEdit/components/LMS/LessonsEditor/helpers';
import { normalizeUrl } from 'shared/helpers/normalizeUrl';
import {
  CalendarLabel,
  ClockIcon,
  DayButton,
  DaysContainer,
  GroupButton,
  GroupsContainer,
  GroupsRow,
  LessonTypeWrapper,
  ModalTimeAndTypeRow,
  StyledRectangularButtonFilled,
  ZoomLink,
} from 'shared/pages/projectEdit/components/LMS/LessonsEditor/styles';
import { ILessonData, ILessonModalData, LMSLessonClassTypeModel } from 'shared/pages/projectEdit/components/LMS/LessonsEditor/LessonsEditorTypes';

const ModalWrapper = styled.div<{ $inline?: boolean }>`
  border-radius: 8px;
  width: 720px;
  padding: 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${({ $inline }) => $inline && `
    width: 100%;
    padding: 0;
    border-radius: 0;
    gap: 16px;
  `}

`;

const ModalHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;

const ModalCloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.5;
  }
`;
const ModalTitleText = styled.div`
  font-family: Inter;
  font-size: 28px;
  font-weight: 600;
  line-height: 34px;
  letter-spacing: -0.56px;
  color: #151514;

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 18px;
    line-height: 24px;
  }
`;

const ModalSubtitleText = styled.div`
  font-family: Inter;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  color: #909090;
  letter-spacing: -0.3px;
  margin-top: 4px;
`;
const InlineLessonFieldsRow = styled.div`
  display: grid;
  grid-template-columns: 336px 140px 140px minmax(180px, 1fr);
  gap: 24px;
  align-items: end;
  width: 100%;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    grid-template-columns: 280px 100px 100px minmax(160px, 1fr);
    gap: 16px;
  }

  @media (max-width: ${LayoutConst.screen.md}px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    > :first-child,
    > :last-child {
      grid-column: span 2;
    }
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    grid-template-columns: 1fr;

    > :first-child,
    > :last-child {
      grid-column: auto;
    }
  }
`;

const TimeInputWrapper = styled(FlexBox)<{ $inline?: boolean }>`
  position: relative;
  width: 120px;
  height: 48px;
  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    width: 100px;
    height: 40px;
    }
  @media (max-width: ${LayoutConst.screen.m}px) {
      width: 195px;
    }
  @media (max-width: ${LayoutConst.screen.s}px) {
      width: 100%;
    }

  input[type="time"] {
    width: 100%;
    padding: 14px 16px;
    font-family: Inter;
    font-size: 15px;
    font-weight: 400;
    line-height: 20px;
    color: #151514;
    background-color: ${({ $inline }) => ($inline ? '#FFFFFF' : '#F2F2F2')};
    border: none;
    border-radius: 3px;
    outline: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;

    &::-webkit-calendar-picker-indicator {
      display: none;
    }

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
      font-size: 13px;
      line-height: 16px;
      padding: 10px 36px 10px 14px;
    }

  }
`;
const ZoomInputWrapper = styled(FlexBox)<{ $inline?: boolean }>`
  width: 185px;
  height: 48px;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    width: 150px;
    height: 40px;
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    width: 100%;
  }

  input {
    width: 100%;
    padding: 12px 16px;
    font-family: Inter;
    font-size: 15px;
    font-weight: 400;
    line-height: 20px;
    color: #151514;
    background-color: ${({ $inline }) => ($inline ? '#FFFFFF' : '#F2F2F2')};
    border: none;
    border-radius: 3px;
    outline: none;

    &::placeholder {
      color: #909090;
    }

    @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
      font-size: 13px;
      line-height: 16px;
      padding: 10px 14px;
    }
  }
`;

const ClockIconComponent: FC<{ onClick?: () => void }> = ({ onClick }) => (
  <ClockIcon onClick={onClick}>
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0ZM8 14.5C4.41015 14.5 1.5 11.5899 1.5 8C1.5 4.41015 4.41015 1.5 8 1.5C11.5899 1.5 14.5 4.41015 14.5 8C14.5 11.5899 11.5899 14.5 8 14.5Z" fill="#909090"/>
      <path d="M8.5 4H7.5V8.5H11.5V7.5H8.5V4Z" fill="#909090"/>
    </svg>
  </ClockIcon>
);


export interface LessonAddModalProps {
  courseStartDate: Date | null;
  courseEndDate: Date | null;
  courseGroups: Array<{ groupId: string; name: Array<{ lang: string; name: string }> }>;
  lessonTypeOptions: Array<{ value: string; label: string }>;
  getGroupName: (group: { groupId: string; name: Array<{ lang: string; name: string }> }) => string;
  onSubmit: (data: ILessonData, onSuccess: () => void) => void;
  inline?: boolean;
}

export const LessonAddModal: FC<LessonAddModalProps> = ({
  courseStartDate,
  courseEndDate,
  courseGroups,
  lessonTypeOptions,
  getGroupName,
  onSubmit,
  inline = false,
}) => {
  const { t } = useTranslation();
  const modal = useModal();
  const toasts = useToasts();

  const startTimeRef = useRef<HTMLInputElement>(null);
  const endTimeRef = useRef<HTMLInputElement>(null);

  const { control, watch, setValue, handleSubmit } = useForm<ILessonModalData>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    shouldUnregister: false,
    defaultValues: {
      selectedDays: [],
      start: '',
      end: '',
      zoomLink: '',
      selectedGroups: [],
      selectedLessonType: LMSLessonClassTypeModel.Ordinal,
    },
  });

  const formData = watch();

  const handleDaySelect = useCallback((dayValue: number) => {
    const currentDays = watch('selectedDays') || [];
    const isSelected = currentDays.includes(dayValue);
    setValue(
      'selectedDays',
      isSelected ? currentDays.filter((d) => d !== dayValue) : [...currentDays, dayValue],
      { shouldValidate: false, shouldDirty: false, shouldTouch: false },
    );
  }, [watch, setValue]);

  const handleGroupToggle = useCallback((groupId: string) => {
    const currentGroups = watch('selectedGroups') || [];
    const isSelected = currentGroups.includes(groupId);
    setValue(
      'selectedGroups',
      isSelected ? currentGroups.filter((id) => id !== groupId) : [...currentGroups, groupId],
      { shouldValidate: false, shouldDirty: false, shouldTouch: false },
    );
  }, [watch, setValue]);

  const handleStartTimeClick = useCallback(() => {
    startTimeRef.current?.showPicker?.();
    startTimeRef.current?.focus();
  }, []);

  const handleEndTimeClick = useCallback(() => {
    endTimeRef.current?.showPicker?.();
    endTimeRef.current?.focus();
  }, []);

  const handleLocalSubmit = useCallback((data: ILessonModalData) => {
    onSubmit(
      {
        ...data,
        courseStartDate,
        courseEndDate,
      },
      () => {
        if (!inline) {
          modal.hide();
        }
      },
    );
  }, [onSubmit, courseStartDate, courseEndDate, inline, modal]);

  const daysField = (
    <FlexBox direction="column" gy={8}>
      <CalendarLabel>{t('lms.lessonsEditor.selectDayLabel')}</CalendarLabel>
      <DaysContainer style={{ height: 'auto', flexWrap: 'wrap' }}>
        {[
          { label: t('lms.lessonsEditor.weekDays.mon'), value: 1 },
          { label: t('lms.lessonsEditor.weekDays.tue'), value: 2 },
          { label: t('lms.lessonsEditor.weekDays.wed'), value: 3 },
          { label: t('lms.lessonsEditor.weekDays.thu'), value: 4 },
          { label: t('lms.lessonsEditor.weekDays.fri'), value: 5 },
          { label: t('lms.lessonsEditor.weekDays.sat'), value: 6 },
          { label: t('lms.lessonsEditor.weekDays.sun'), value: 0 },
        ].map((day) => (
          <DayButton
            key={day.value}
            isSelected={formData.selectedDays.includes(day.value)}
            onClick={() => handleDaySelect(day.value)}
          >
            {day.label}
          </DayButton>
        ))}
      </DaysContainer>
    </FlexBox>
  );

  const startTimeField = (
    <FlexBox direction="column" gy={8}>
      <CalendarLabel>{t('lms.lessonsEditor.startTimeLabel')}</CalendarLabel>
      <TimeInputWrapper $inline={inline} style={{ width: '100%' }}>
        <Controller
          name="start"
          control={control}
          render={({ field }) => (
            <input
              type="time"
              value={field.value}
              ref={(element) => {
                field.ref(element);
                (startTimeRef as React.MutableRefObject<HTMLInputElement | null>).current = element;
              }}
              onChange={(event) => {
                const newStartTime = event.target.value;
                const endTime = watch('end');
                if (endTime && newStartTime && endTime < newStartTime) {
                  setValue('end', '', { shouldValidate: false, shouldDirty: false });
                  toasts.info(t('lms.lessonsEditor.endTimeResetInfo'));
                }
                field.onChange(newStartTime);
              }}
            />
          )}
        />
        <ClockIconComponent onClick={handleStartTimeClick} />
      </TimeInputWrapper>
    </FlexBox>
  );

  const endTimeField = (
    <FlexBox direction="column" gy={8}>
      <CalendarLabel>{t('lms.lessonsEditor.endTimeLabel')}</CalendarLabel>
      <TimeInputWrapper $inline={inline} style={{ width: '100%' }}>
        <Controller
          name="end"
          control={control}
          render={({ field }) => (
            <input
              type="time"
              value={field.value}
              min={formData.start || undefined}
              ref={(element) => {
                field.ref(element);
                (endTimeRef as React.MutableRefObject<HTMLInputElement | null>).current = element;
              }}
              onChange={(event) => {
                const newEndTime = event.target.value;
                const startTime = watch('start');
                if (startTime && newEndTime && newEndTime < startTime) {
                  toasts.error(t('lms.lessonsEditor.endTimeBeforeStartError'));
                  field.onChange('');
                  return;
                }
                field.onChange(newEndTime);
              }}
            />
          )}
        />
        <ClockIconComponent onClick={handleEndTimeClick} />
      </TimeInputWrapper>
    </FlexBox>
  );

  const zoomLinkField = (
    <FlexBox direction="column" gy={8}>
      <CalendarLabel>{t('lms.lessonsEditor.zoomLinkLabel')}</CalendarLabel>
      <ZoomInputWrapper $inline={inline} style={{ width: '100%' }}>
        <Controller
          name="zoomLink"
          control={control}
          render={({ field }) => (
            <input
              type="text"
              placeholder={t('lms.lessonsEditor.enterData')}
              {...field}
              onChange={(event) => {
                const filteredValue = event.target.value.replace(/[А-Яа-яЁё]/g, '');
                field.onChange(filteredValue);
              }}
            />
          )}
        />
      </ZoomInputWrapper>
      {formData.zoomLink && isValidUrl(formData.zoomLink) && (
        <ZoomLink
          href={normalizeUrl(formData.zoomLink)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => event.stopPropagation()}
          style={{ maxWidth: '100%' }}
        >
          {normalizeUrl(formData.zoomLink)}
        </ZoomLink>
      )}
    </FlexBox>
  );

  return (
    <ModalWrapper $inline={inline}>
      {!inline && (
        <ModalHeaderRow>
          <div>
            <ModalTitleText>{t('lms.lessonsEditor.modalTitle')}</ModalTitleText>
            <ModalSubtitleText>{t('lms.lessonsEditor.subtitle')}</ModalSubtitleText>
          </div>
          <ModalCloseBtn onClick={() => modal.hide()} aria-label="close">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M0.298618 0.154297C0.504347 -0.0514325 0.837901 -0.0514325 1.04363 0.154297L8.57311 7.68378L16.4561 0.154298C16.6619 -0.0514323 16.9954 -0.0514321 17.2012 0.154298L17.3455 0.298619C17.5512 0.504348 17.5512 0.8379 17.3455 1.04363L9.816 8.57311C9.76911 8.61999 9.74277 8.68358 9.74277 8.74989C9.74277 8.81619 9.76911 8.87978 9.816 8.92666L17.3455 16.4561C17.5524 16.6631 17.5507 16.9967 17.3451 17.1956C17.2768 17.2616 17.2393 17.2967 17.2177 17.316C16.9766 17.5327 16.643 17.5324 16.4419 17.3312L8.92666 9.81599C8.82903 9.71836 8.67074 9.71836 8.57311 9.81599L1.04364 17.3455C0.836674 17.5524 0.503017 17.5507 0.30421 17.3451C0.238131 17.2768 0.203119 17.2393 0.183765 17.2177C-0.0329741 16.9766 -0.0326004 16.643 0.16857 16.4419L7.68377 8.92666C7.73066 8.87978 7.757 8.81619 7.757 8.74989C7.757 8.68358 7.73066 8.61999 7.68378 8.57311L0.154297 1.04363C-0.0514323 0.837901 -0.0514324 0.504348 0.154297 0.298618L0.298618 0.154297Z" fill="#151514"/>
            </svg>
          </ModalCloseBtn>
        </ModalHeaderRow>
      )}

      {inline ? (
        <InlineLessonFieldsRow>
          {daysField}
          {startTimeField}
          {endTimeField}
          {zoomLinkField}
        </InlineLessonFieldsRow>
      ) : (
        <>
          {daysField}
          <ModalTimeAndTypeRow>
            {startTimeField}
            {endTimeField}
            <FlexBox direction="column" gy={8}>
              <CalendarLabel>{t('lms.lessonsEditor.lessonTypeLabel')}</CalendarLabel>
              <LessonTypeWrapper style={{ width: '100%' }}>
                <Controller
                  name="selectedLessonType"
                  control={control}
                  render={({ field }) => (
                    <CommonInput
                      type={INPUT_TYPES.dropdown}
                      orientation="column"
                      options={lessonTypeOptions}
                      value={lessonTypeOptions.find((option) => option.value === String(field.value)) || undefined}
                      onChange={(option: any) => {
                        field.onChange(Number(option?.value ?? LMSLessonClassTypeModel.Ordinal));
                      }}
                      placeholder={t('lms.lessonsEditor.selectLessonType')}
                    />
                  )}
                />
              </LessonTypeWrapper>
            </FlexBox>
            {zoomLinkField}
          </ModalTimeAndTypeRow>
        </>
      )}

      {/* Группы */}
      <FlexBox direction="column" gy={8}>
        <CalendarLabel>{t('lms.lessonsEditor.groupsLabel')}</CalendarLabel>
        <GroupsContainer style={{ maxWidth: '100%' }}>
          <GroupsRow>
            {courseGroups.map((group) => (
              <GroupButton
                key={group.groupId}
                isSelected={formData.selectedGroups.includes(group.groupId)}
                $inline={inline}
                onClick={() => handleGroupToggle(group.groupId)}
              >
                {(() => {
                  const name = getGroupName(group);
                  return name.length > 15 ? `${name.substring(0, 15)}...` : name;
                })()}
              </GroupButton>
            ))}
          </GroupsRow>
        </GroupsContainer>
      </FlexBox>

      <StyledRectangularButtonFilled
        style={{
          width: inline ? '390px' : '100%',
          alignSelf: inline ? 'flex-end' : undefined,
        }}
        onClick={handleSubmit(handleLocalSubmit)}
      >
        {t('lms.lessonsEditor.addLessonButton')}
      </StyledRectangularButtonFilled>

    </ModalWrapper>
  );
};
